import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import { StatCard } from "@/components/reports/stat-card";
import { BarChart } from "@/components/reports/bar-chart";

export const Route = createFileRoute("/_authenticated/reports/permits")({
  component: PermitsReport,
});

interface PermitComplianceData {
  byStatus: {
    draft: number;
    active: number;
    completed: number;
    cancelled: number;
    expired: number;
  };
  closeOutRate: number;
  total: number;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-400",
  active: "bg-green-500",
  completed: "bg-blue-500",
  cancelled: "bg-yellow-500",
  expired: "bg-red-500",
};

function PermitsReport() {
  const params = new URLSearchParams(window.location.search);
  const dateFrom = params.get("dateFrom") || daysAgo(30);
  const dateTo = params.get("dateTo") || today();
  const projectId = params.get("projectId") || undefined;

  const qs = new URLSearchParams({ dateFrom, dateTo });
  if (projectId) qs.set("projectId", projectId);

  const { data, isLoading } = useQuery({
    queryKey: [
      "reports",
      "permit-compliance",
      { dateFrom, dateTo, projectId },
    ],
    queryFn: () =>
      apiGet<{ data: PermitComplianceData }>(
        `/api/reports/permit-compliance?${qs.toString()}`
      ),
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="border rounded-lg p-3 h-20 animate-pulse bg-muted/40"
          />
        ))}
      </div>
    );
  }

  const permits = data?.data;
  if (!permits || permits.total === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No permits found for the selected period.
      </p>
    );
  }

  const closeOutPct = Math.round(permits.closeOutRate * 100);

  // Bar chart items
  const statusItems = Object.entries(permits.byStatus)
    .filter(([, count]) => count > 0)
    .map(([status, count]) => ({
      label: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      color: STATUS_COLORS[status] || "bg-gray-400",
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard label="Total Permits" value={permits.total} />
        <StatCard
          label="Active"
          value={permits.byStatus.active}
          className="border-green-200 bg-green-50/50"
        />
        <StatCard
          label="Completed"
          value={permits.byStatus.completed}
          barPct={
            permits.total > 0
              ? Math.round((permits.byStatus.completed / permits.total) * 100)
              : 0
          }
          barColor="bg-blue-500"
        />
        <StatCard
          label="Cancelled"
          value={permits.byStatus.cancelled}
          className={
            permits.byStatus.cancelled > 0
              ? "border-yellow-200 bg-yellow-50/50"
              : undefined
          }
        />
        <StatCard
          label="Expired"
          value={permits.byStatus.expired}
          className={
            permits.byStatus.expired > 0
              ? "border-red-200 bg-red-50/50"
              : undefined
          }
        />
        <StatCard
          label="Close-out Rate"
          value={`${closeOutPct}%`}
          barPct={closeOutPct}
          barColor={closeOutPct >= 80 ? "bg-green-500" : closeOutPct >= 50 ? "bg-yellow-500" : "bg-red-500"}
        />
      </div>

      {/* Status distribution chart */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Status Distribution
        </h3>
        <BarChart items={statusItems} />
      </div>
    </div>
  );
}
